/**
 * Аутентификация через микросервис api-user.
 *
 * Эндпоинты (проверено 2026-05-14 на реальном API):
 *   POST /api-user/api/v1/users/profile/signup  {email,password,firstName}
 *        → 201 {message:"Пользователь создан"}
 *   POST /api-user/api/v1/users/profile/login   {email,password}
 *        → {token, refreshToken}   — username/имя НЕ возвращается!
 *   GET  /api-user/api/v1/users/profile  (Authorization)
 *        → {data:{id,firstName,email,photo,...}}  — отсюда берём имя
 *   GET  /api-user/api/v1/users/diamond     (Authorization) → {count}
 *   GET  /api-user/api/v1/users/rating/me   (Authorization) → {rating,userId}
 *
 * JWT payload содержит только {id, exp}. user_id = поле `id`.
 * Имя пользователя в payload отсутствует — догружаем через /profile.
 */

import { currentUserId, decodeTokenPayload } from "./identity";
import * as v2Api from "./v2";
import {
  request,
  setTokens,
  clearTokens,
  getToken,
  getRefreshToken,
  refreshOnce,
  API_BASE,
} from "./client";

const U = "/api-user/api/v1/users";

/* ── регистрация ─────────────────────────────────────────────────── */

/**
 * Регистрирует аккаунт. Токен НЕ возвращается — после signup нужно login.
 * @returns {Promise<void>}
 */
export async function signup({
  email, password, firstName, birthDate, gender, website,
  captchaIdLeft, captchaAnswerLeft,
  captchaIdRight, captchaAnswerRight,
  canvasData,
}) {
  // birthDate ("YYYY-MM-DD") и gender ("male"/"female") — опциональны.
  // website — honeypot для антибота.
  // Тройная капча: два изображения + рисунок кота — все три обязательны.
  await request(`${U}/profile/signup`, {
    method: "POST",
    body: {
      email, password, firstName, birthDate, gender,
      website: website || "",
      captchaIdLeft: captchaIdLeft || "",
      captchaAnswerLeft: captchaAnswerLeft || "",
      captchaIdRight: captchaIdRight || "",
      captchaAnswerRight: captchaAnswerRight || "",
      canvasData: canvasData || "",
    },
  });
}

/* ── adaptive captcha ──────────────────────────────────────────────── */

/** Узнать, требуется ли сейчас капча. Кэшируется фронтом.
 * ВАЖНО: используем API_BASE (minecraftia-school.ru), потому что rublox.pro
 * не проксирует /api-user/ — раньше fetch(`${U}/captcha/required`) шёл
 * относительно текущего домена и падал 404, картинка не загружалась. */
export async function isCaptchaRequired() {
  try {
    const r = await fetch(`${API_BASE}${U}/captcha/required`);
    if (!r.ok) return false;
    const j = await r.json();
    return Boolean(j && j.required);
  } catch {
    return false;
  }
}

/** Получить новый challenge капчи: {id, image: dataUrl}. */
export async function getCaptchaChallenge() {
  const r = await fetch(`${API_BASE}${U}/captcha/challenge`);
  if (!r.ok) throw new Error("Не удалось получить капчу");
  return r.json();
}

/** Получить пару капч (левую + правую) одним запросом: {left:{id,image}, right:{id,image}}. */
export async function getCaptchaChallengePair() {
  const r = await fetch(`${API_BASE}${U}/captcha/challenge-pair`);
  if (!r.ok) throw new Error("Не удалось получить капчу");
  return r.json();
}

/* ── вход ────────────────────────────────────────────────────────── */

/**
 * Выполняет вход, сохраняет токены в localStorage,
 * затем догружает профиль (имя/фото — их нет в ответе login).
 * @returns {Promise<{token,username,userId,photo}>}
 */
export async function login({ email, password }) {
  const data = await request(`${U}/profile/login`, {
    method: "POST",
    body: { email, password },
  });
  const { token, refreshToken } = data || {};
  if (!token) {
    throw new Error("Сервер не вернул токен авторизации.");
  }
  setTokens(token, refreshToken);

  const userId = userIdFromToken(token);
  // имя/фото берём из профиля; если профиль не отдался — не валим вход
  let username = null;
  let photo = null;
  try {
    const profile = await getProfile();
    username = profile.firstName || null;
    photo = profile.photo || null;
  } catch {
    /* профиль недоступен — войдём без имени, шапка покажет «Профиль» */
  }
  return { token, username, userId, photo };
}

/**
 * Регистрация + сразу вход — удобный комбо для формы signup.
 * После входа выдаёт дефолтный скин по полу (storys grant-default-skin).
 */
export async function signupAndLogin({
  email,
  password,
  firstName,
  birthDate,
  gender,
  website,
  captchaIdLeft,
  captchaAnswerLeft,
  captchaIdRight,
  captchaAnswerRight,
  canvasData,
}) {
  await signup({
    email, password, firstName, birthDate, gender, website,
    captchaIdLeft, captchaAnswerLeft,
    captchaIdRight, captchaAnswerRight,
    canvasData,
  });
  const session = await login({ email, password });

  // выдаём бесплатный дефолтный скин в зависимости от пола;
  // не критично — если упадёт, регистрацию не валим
  if (session.userId && gender) {
    try {
      await grantDefaultSkin(session.userId, gender);
    } catch {
      /* скин выдадим позже / приложение само подставит дефолт */
    }
  }

  // Код подтверждения НЕ шлём автоматически — пользователю предлагается
  // подтвердить email в модалке после регистрации, код уходит только
  // если он нажмёт кнопку (см. SignupPage).
  return session;
}

/**
 * Выдаёт пользователю бесплатный дефолтный скин Рублокса по полу.
 * Идемпотентно на стороне бэкенда.
 */
export async function grantDefaultSkin(userId, gender) {
  await request("/api-storys/kubikon3d/rublox/grant-default-skin", {
    method: "POST",
    body: { user_id: userId, gender },
  });
}

/* ── выход ───────────────────────────────────────────────────────── */

export function logout() {
  clearTokens();
}

/* ── разбор JWT ──────────────────────────────────────────────────── */

/**
 * Декодирует payload JWT. Возвращает null, если токен битый/истёк.
 */
export function decodeJwt(token) {
  // Разбор — в api/identity.js (единая точка на весь сайт). Здесь только
  // проверка срока: истёкший токен считаем невалидным. Подпись не
  // проверяем — это дело бэкенда, поэтому смена HS256→RS256 ничего
  // не ломает на клиенте.
  const payload = decodeTokenPayload(token);
  if (!payload) return null;
  if (payload.exp && payload.exp * 1000 < Date.now()) return null;
  return payload;
}

/** user_id из токена. Разбор — в api/identity.js (единая точка на весь сайт),
 *  здесь только реэкспорт для старых импортов. */
export function userIdFromToken(token) {
  return currentUserId(token);
}

/** Есть ли валидный (не истёкший) токен в хранилище. */
export function isAuthenticated() {
  return decodeJwt(getToken()) !== null;
}

/**
 * Восстанавливает сессию из localStorage при загрузке сайта.
 * Синхронная часть: проверяет токен, отдаёт userId.
 * Имя/фото нужно догрузить отдельно через getProfile() (см. AuthContext).
 * @returns {{token,userId}|null}
 */
export function restoreSession() {
  const token = getToken();
  const payload = decodeJwt(token);
  if (!payload) return null;
  return {
    token,
    userId: currentUserId(token),
  };
}

/**
 * Восстановление сессии с попыткой продлить её.
 *
 * Раньше здесь при истёкшем access вызывался clearTokens() — вместе с ним
 * выбрасывался и refresh, хотя сессию можно было продлить. Пользователя
 * выкидывало на ровном месте: access живёт 24 часа, refresh — 30 дней.
 *
 * Теперь: access жив → работаем сразу; истёк, но есть refresh → обновляем
 * и продолжаем; refresh отвергнут сервером → только тогда чистим.
 * При недоступном бэкенде (5xx/сеть) токены НЕ трогаем — см. doRefresh.
 *
 * @returns {Promise<{token,userId}|null>}
 */
export async function restoreSessionAsync() {
  const direct = restoreSession();
  if (direct) return direct;

  // access протух или его нет — пробуем продлить
  if (!getRefreshToken()) {
    clearTokens();
    return null;
  }
  const ok = await refreshOnce();
  if (!ok) return null; // doRefresh сам почистит, если сессия реально мертва

  return restoreSession();
}

/* ── данные профиля ──────────────────────────────────────────────── */

/**
 * Профиль текущего пользователя.
 * Ответ обёрнут в {data:{...}} — разворачиваем.
 * @returns {Promise<{id,firstName,email,photo,...}>}
 */
export async function getProfile(signal) {
  // Сначала v2 (единый источник правды по аккаунтам), при неудаче — v1.
  // Фолбэк нужен на время переезда: если identity недоступен, сайт
  // продолжает работать на старом сервисе, а не показывает пустой профиль.
  try {
    const me = await v2Api.getMe(signal);
    if (me && me.id) return adaptV2Profile(me);
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    // 401 не глушим: он означает «токен мёртв», и v1 ответит тем же.
    if (e?.status === 401) throw e;
  }
  const res = await request(`${U}/profile`, { auth: true, signal });
  return (res && res.data) || {};
}

/**
 * Приводит профиль v2 к форме, которую ждёт остальной код сайта.
 * v1 отдаёт firstName/photo, v2 — nickname/avatar_url. Переименование
 * держим в ОДНОМ месте, а не правим полсотни мест использования.
 */
function adaptV2Profile(me) {
  return {
    ...me,
    firstName: me.nickname ?? null,
    photo: me.avatar_url ?? null,
    coverPhoto: me.cover_url ?? null,
  };
}

/** Баланс алмазов текущего пользователя. */
export async function getDiamonds(signal) {
  try {
    return await v2Api.getDiamonds(signal);
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    if (e?.status === 401) throw e;
  }
  const data = await request(`${U}/diamond`, { auth: true, signal });
  return data && typeof data.count === "number" ? data.count : 0;
}

/** Рейтинг текущего пользователя. */
export async function getRating(signal) {
  const data = await request(`${U}/rating/me`, { auth: true, signal });
  return data && typeof data.rating === "number" ? data.rating : 0;
}

/* ── подтверждение email + смена email/пароля ───────────────────── */

/** Статус подтверждения аккаунта. @returns {Promise<{confirmed:boolean}>} */
export async function getAuthStatus(signal) {
  try {
    const data = await request(`${U}/auth`, { auth: true, signal });
    return { confirmed: !!(data && data.accountConfirmation) };
  } catch {
    return { confirmed: false };
  }
}

/**
 * Запросить authorize_url для OAuth подтверждения текущего залогиненного
 * аккаунта через Яндекс. Возвращает { ok, authorizeUrl } | { ok:false, message }.
 */
export async function startYandexVerify() {
  try {
    const frontend = window.location.origin;
    const data = await request(
      `${U}/auth/yandex/start?frontend=${encodeURIComponent(frontend)}`,
      { method: "GET" },
    );
    return { ok: true, authorizeUrl: data?.authorize_url };
  } catch (e) {
    return { ok: false, message: e?.message || "Не удалось начать подтверждение" };
  }
}

/**
 * Verify-обмен: подтверждение email текущего залогиненного юзера через Яндекс.
 * confirmEmailChange=true → согласие на смену email на Яндекс-адрес.
 * Может вернуть:
 *   { ok:true, verified, email, emailChanged }
 *   { ok:true, needsEmailChangeConfirmation, currentEmail, yandexEmail, message }
 *   { ok:false, message }
 */
export async function exchangeYandexVerify({ code, state, confirmEmailChange = false, confirmToken = null }) {
  try {
    // Второй шаг (юзер согласился сменить email) шлёт confirm_token вместо
    // code/state — они одноразовые, повторно через Яндекс не обменять.
    const body = (confirmEmailChange && confirmToken)
      ? { confirm_token: confirmToken, confirm_email_change: true }
      : { code, state, confirm_email_change: !!confirmEmailChange };
    const data = await request(`${U}/auth/yandex/exchange`, {
      method: "POST",
      body,
      auth: true,
    });
    if (data?.needs_email_change_confirmation) {
      return {
        ok: true,
        needsEmailChangeConfirmation: true,
        confirmToken: data.confirm_token,
        currentEmail: data.current_email,
        yandexEmail: data.yandex_email,
        message: data.message,
      };
    }
    return {
      ok: true,
      verified: !!data?.verified,
      email: data?.email,
      emailChanged: !!data?.email_changed,
      message: data?.message,
    };
  } catch (e) {
    return { ok: false, message: e?.message || "Не удалось подтвердить через Яндекс" };
  }
}

/**
 * Завершение OAuth-входа: обмен authorization code на наш JWT.
 * Используется в YandexCallbackPage. Сохраняет токены и догружает профиль —
 * аналогично обычному login.
 */
export async function exchangeYandexCode({ code, state }) {
  const data = await request(`${U}/auth/yandex/exchange`, {
    method: "POST",
    body: { code, state },
  });
  const { token, refreshToken } = data || {};
  if (!token) {
    throw new Error("Сервер не вернул токен авторизации.");
  }
  setTokens(token, refreshToken);

  const userId = userIdFromToken(token);
  let username = null;
  let photo = null;
  try {
    const profile = await getProfile();
    username = profile.firstName || null;
    photo = profile.photo || null;
  } catch {
    /* профиль не отдался — войдём без имени */
  }
  return { token, username, userId, photo, isNew: !!data.isNew };
}

/* ── Google OAuth ──────────────────────────────────────────────────
 * Симметрично Yandex: startGoogleAuth / exchangeGoogleVerify / exchangeGoogleCode.
 * Бэкенд: /api-user/api/v1/users/auth/google/{start,exchange}.
 */

/** Начать вход через Google. Возвращает authorize_url. */
export async function startGoogleAuth() {
  try {
    const frontend = window.location.origin;
    const data = await request(
      `${U}/auth/google/start?frontend=${encodeURIComponent(frontend)}`,
      { method: "GET" },
    );
    return { ok: true, authorizeUrl: data?.authorize_url };
  } catch (e) {
    return { ok: false, message: e?.message || "Не удалось начать вход" };
  }
}

/** Verify-обмен через Google (подтверждение email уже залогиненного юзера). */
export async function exchangeGoogleVerify({ code, state, confirmEmailChange = false, confirmToken = null }) {
  try {
    const body = (confirmEmailChange && confirmToken)
      ? { confirm_token: confirmToken, confirm_email_change: true }
      : { code, state, confirm_email_change: !!confirmEmailChange };
    const data = await request(`${U}/auth/google/exchange`, {
      method: "POST",
      body,
      auth: true,
    });
    if (data?.needs_email_change_confirmation) {
      return {
        ok: true,
        needsEmailChangeConfirmation: true,
        confirmToken: data.confirm_token,
        currentEmail: data.current_email,
        googleEmail: data.google_email,
        message: data.message,
      };
    }
    return {
      ok: true,
      verified: !!data?.verified,
      email: data?.email,
      emailChanged: !!data?.email_changed,
      message: data?.message,
    };
  } catch (e) {
    return { ok: false, message: e?.message || "Не удалось подтвердить через Google" };
  }
}

/** Завершение Google-входа (login mode). */
export async function exchangeGoogleCode({ code, state }) {
  const data = await request(`${U}/auth/google/exchange`, {
    method: "POST",
    body: { code, state },
  });
  const { token, refreshToken } = data || {};
  if (!token) {
    throw new Error("Сервер не вернул токен авторизации.");
  }
  setTokens(token, refreshToken);

  const userId = userIdFromToken(token);
  let username = null;
  let photo = null;
  try {
    const profile = await getProfile();
    username = profile.firstName || null;
    photo = profile.photo || null;
  } catch {
    /* профиль не отдался — войдём без имени */
  }
  return { token, username, userId, photo, isNew: !!data.isNew };
}

/** Запросить код подтверждения на email. */
export async function sendConfirmCode() {
  try {
    const data = await request(`${U}/auth/send-confirm-code`, {
      method: "POST", auth: true, body: {},
    });
    return {
      ok: true,
      message: (data && data.message) || "Код отправлен",
      alreadyConfirmed: !!(data && data.already_confirmed),
      email: data && data.email,
    };
  } catch (e) {
    return { ok: false, message: e.message || "Не удалось отправить код" };
  }
}

/** Проверить код подтверждения. */
export async function verifyConfirmCode(code) {
  try {
    const data = await request(`${U}/auth/verify-confirm-code`, {
      method: "POST", auth: true, body: { code },
    });
    return { ok: !!(data && data.confirmed), message: (data && data.message) || "Аккаунт подтверждён" };
  } catch (e) {
    return { ok: false, message: e.message || "Неверный код" };
  }
}

/** Сменить email. Требует ввода текущего пароля. */
export async function changeEmail(password, newEmail) {
  try {
    const data = await request(`${U}/profile/email`, {
      method: "PUT", auth: true, body: { password, newEmail },
    });
    return { ok: true, message: (data && data.message) || "Email изменён", email: data && data.email };
  } catch (e) {
    return { ok: false, message: e.message || "Ошибка изменения email" };
  }
}

/** Сменить пароль. Бэк проверяет текущий пароль. */
export async function changePassword(oldPass, newPass) {
  try {
    const data = await request(`${U}/profile/pass`, {
      method: "PUT", auth: true, body: { oldPass, newPass },
    });
    return { ok: true, message: (data && data.message) || "Пароль изменён" };
  } catch (e) {
    return { ok: false, message: e.message || "Ошибка изменения пароля" };
  }
}
